import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { isNativeShell } from '../lib/nativeBridge';

/**
 * "A newer version of the app is available."
 *
 * Almost everything in the app is this web page, which is always current. Only
 * the shell around it — microphone, camera, sign-in — needs installing again,
 * so the shell reports its build and the newest build is listed in
 * /app-version.json. Raising a number there is what makes the prompt appear;
 * nothing has to be rebuilt to start or stop it. A platform with no number
 * listed is never prompted, which is how iOS stays quiet until the new build
 * is live on the App Store.
 */

type Listing = { latestBuild: number | null; url: string };
const DISMISS_KEY = 'blisx.updateDismissed';

export default function AppUpdateBanner() {
  const [offer, setOffer] = useState<{ url: string; latest: number } | null>(null);

  useEffect(() => {
    if (!isNativeShell()) return;
    const app = (window as { __BLISX_APP__?: { platform?: string; build?: number } }).__BLISX_APP__;
    const platform = app?.platform ?? (/Android/i.test(navigator.userAgent) ? 'android' : 'ios');
    // Builds from before this check send nothing: they are older than any listed.
    const build = Number(app?.build ?? 0) || 0;
    let cancelled = false;
    fetch(`/app-version.json?t=${Date.now()}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((all: Record<string, Listing> | null) => {
        const listing = all?.[platform];
        if (cancelled || !listing?.latestBuild || build >= listing.latestBuild) return;
        try {
          if (sessionStorage.getItem(DISMISS_KEY) === String(listing.latestBuild)) return;
        } catch {
          // Storage unavailable: show it.
        }
        setOffer({ url: listing.url, latest: listing.latestBuild });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!offer) return null;

  return (
    <div className="border-b border-sky-900 bg-sky-950/70">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2 text-sm text-sky-100">
        <span className="min-w-0 flex-1">A newer version of the BlisxBench app is available.</span>
        <a
          href={offer.url}
          className="shrink-0 rounded-md bg-accent-500 px-3 py-1.5 font-medium text-white hover:bg-accent-400"
        >
          Update
        </a>
        <button
          className="icon-btn shrink-0 text-sky-300 hover:text-sky-100"
          aria-label="Not now"
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, String(offer.latest));
            } catch {
              // Not remembered; it simply shows again next time.
            }
            setOffer(null);
          }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
